import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { portalLinks, pageContext, roleLabel, SOFMAT_SPACES } from "@/lib/sofmat-navigation";
import {
  LayoutDashboard, Users, FileStack, Settings2, Wrench, ScrollText,
  UserCog, LogOut, CreditCard, Menu, X, ClipboardCheck, Euro,
  PackageCheck, FlaskConical, ChevronDown, ChevronRight, Check,
  Mail, LifeBuoy, Building2, GraduationCap, FileText, House,
} from "lucide-react";
import "./SofmatShell.css";
import SofmatOnboarding from "./SofmatOnboarding";

const nav = [
  { key: "dashboard", to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard", group: "Lavoro" },
  { key: "customers", to: "/customers", label: "Clienti", icon: Users, testId: "nav-customers", group: "Lavoro" },
  { key: "rfqs", to: "/rfqs", label: "Richieste", icon: FileStack, testId: "nav-rfqs", group: "Lavoro" },
  { key: "quotes", to: "/quotes", label: "Preventivi", icon: ScrollText, testId: "nav-quotes", group: "Lavoro" },
  { key: "reader-queue", to: "/reader-queue", label: "Letture dei disegni", icon: ClipboardCheck, testId: "nav-reader-queue", group: "Lavoro" },
  { key: "operational-archive", to: "/operations/archive", label: "Archivio riservato", icon: PackageCheck, testId: "nav-operational-archive", group: "Lavoro", admin: true },
  // A visible entry still requires BOTH the original Owner/Admin route guard
  // and explicit backend confirmation that this is the collaudo tenant.
  { key: "collaudo-lettore", to: "/collaudo-lettore", label: "Collaudo lettore", icon: FlaskConical, testId: "nav-collaudo-lettore", group: "Configurazione", admin: true, requires: "collaudo" },
  { key: "config", to: "/config", label: "Configurazione costi", icon: Wrench, testId: "nav-config", group: "Configurazione" },
  { key: "price-lists", to: "/price-lists", label: "Listini e costi", icon: Euro, testId: "nav-price-lists", group: "Configurazione" },
  { key: "machines", to: "/machines", label: "Parco macchine", icon: Wrench, testId: "nav-machines", group: "Configurazione" },
  { key: "billing", to: "/billing", label: "Piano e fatturazione", icon: CreditCard, testId: "nav-billing", group: "Configurazione" },
  { key: "users", to: "/users", label: "Utenti e ruoli", icon: UserCog, testId: "nav-users", group: "Configurazione", admin: true },
  { key: "audit", to: "/audit", label: "Registro attività", icon: ScrollText, testId: "nav-audit", group: "Configurazione", admin: true },
  { key: "settings", to: "/settings", label: "Impostazioni azienda", icon: Settings2, testId: "nav-settings", group: "Configurazione" },
];
const spaceIcons = { account: House, workshop: Wrench, supplyhub: Building2, academy: GraduationCap, quoteos: FileText };
const closeDisclosureOnBlur = event => {
  const details = event.currentTarget;
  window.setTimeout(() => { if (!details.contains(document.activeElement)) details.open = false; }, 0);
};

// Kept for the login and other existing consumers. The shell uses the parent
// SOFMAT wordmark, with QuoteOS identified as the selected application.
export const BrandLogo = ({ className = "h-8", testId = "brand-logo" }) => (
  <img src="/brand/sofmat-quoteos-logo.png" alt="SOFMAT QuoteOS" className={`${className} w-auto select-none`} draggable={false} data-testid={testId} />
);

function Sidebar({ mobile = false, items, current, links, companyName, onClose, drawerRef, closeRef, loggingOut, onLogout }) {
  return (
    <aside
      className={`qos-sidebar${mobile ? " qos-sidebar-mobile" : " qos-sidebar-desktop"}`}
      data-testid={mobile ? "sidebar-mobile" : "sidebar"}
      id={mobile ? "qos-mobile-navigation" : "qos-desktop-navigation"}
      ref={mobile ? drawerRef : undefined}
      role={mobile ? "dialog" : undefined}
      aria-modal={mobile ? "true" : undefined}
      aria-label="Navigazione QuoteOS"
      tabIndex={mobile ? -1 : undefined}
    >
      <div className="qos-sidebar-heading">
        <a href={links.account} className="qos-wordmark" data-testid={mobile ? "brand-logo-mobile" : "brand-logo-sidebar"}>
          SOFMAT<span>LA RETE DELL’OFFICINA</span>
        </a>
        {mobile && <button type="button" className="qos-icon-button qos-drawer-close" aria-label="Chiudi menu" data-testid="sidebar-close-btn" ref={closeRef} onClick={() => onClose(true)}><X aria-hidden="true" size={21} /></button>}
      </div>
      <p className="qos-sidebar-space">QuoteOS</p>
      <p className="qos-sidebar-company" data-testid="sidebar-tenant" title={companyName}>{companyName}</p>
      <nav className="qos-nav" aria-label="Sezioni QuoteOS">
        {["Lavoro", "Configurazione"].map(group => (
          <div className="qos-nav-group" key={group}>
            <p className="qos-nav-caption">{group}</p>
            {items.filter(item => item.group === group).map(({ key, to, label, icon: Icon, testId }) => (
              <Link key={to} to={to} data-testid={`${testId}-${mobile ? "mobile" : "desktop"}`} className={`qos-nav-item${current.active === key ? " is-active" : ""}`} aria-current={current.active === key ? "page" : undefined} onClick={() => { if (mobile) onClose(false); }}>
                <Icon aria-hidden="true" size={19} strokeWidth={1.7} /><span>{label}</span>
              </Link>
            ))}
          </div>
        ))}
      </nav>
      <div className="qos-sidebar-bottom">
        <a href={links.account}><House aria-hidden="true" size={19} /><span>Il tuo spazio</span></a>
        <a href={links.support}><LifeBuoy aria-hidden="true" size={19} /><span>Assistenza SOFMAT</span></a>
        <button type="button" data-testid={mobile ? "btn-logout-mobile" : "btn-logout-desktop"} onClick={onLogout} disabled={loggingOut}>
          <LogOut aria-hidden="true" size={18} /><span>{loggingOut ? "Uscita in corso…" : "Esci da QuoteOS"}</span>
        </button>
      </div>
    </aside>
  );
}

export default function Layout() {
  const { user, tenant, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const current = pageContext(location.pathname);
  const links = portalLinks(tenant);
  const companyName = (typeof tenant?.name === "string" && tenant.name.trim()) || "Azienda da verificare";
  const personName = (typeof user?.name === "string" && user.name.trim()) || "Il tuo account";
  const administrator = ["owner", "admin"].includes(user?.role);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collaudo, setCollaudo] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState("");
  const workspaceRef = useRef(null), dockRef = useRef(null), skipRef = useRef(null), mainRef = useRef(null);
  const drawerRef = useRef(null), closeRef = useRef(null), openerRef = useRef(null), returnFocus = useRef(true);
  const spacesRef = useRef(null), accountRef = useRef(null);
  const closeDrawer = useCallback((restoreFocus = true) => { returnFocus.current = restoreFocus; setMobileOpen(false); }, []);

  useEffect(() => {
    const previous = document.title;
    return () => { document.title = previous; };
  }, []);
  useEffect(() => {
    closeDrawer(false);
    if (spacesRef.current) spacesRef.current.open = false;
    if (accountRef.current) accountRef.current.open = false;
    document.title = `${pageContext(location.pathname).label} · QuoteOS · SOFMAT`;
    const frame = window.requestAnimationFrame(() => {
      // A quick menu opening must not lose focus to a pending route animation.
      if (!drawerRef.current) mainRef.current?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.pathname, closeDrawer]);
  useEffect(() => {
    let live = true;
    setCollaudo(false);
    if (user?.id && administrator) {
      api.get("/collaudo-lettore/availability").then(({ data }) => { if (live) setCollaudo(data?.available === true); }).catch(() => { if (live) setCollaudo(false); });
    }
    return () => { live = false; };
  }, [user?.id, user?.tenant_id, administrator]);
  useEffect(() => {
    const media = window.matchMedia("(min-width: 981px)");
    const adapt = () => { if (media.matches) closeDrawer(false); };
    if (media.addEventListener) media.addEventListener("change", adapt); else media.addListener(adapt);
    return () => { if (media.removeEventListener) media.removeEventListener("change", adapt); else media.removeListener(adapt); };
  }, [closeDrawer]);
  useEffect(() => {
    const disclosures = [spacesRef, accountRef];
    const outside = event => disclosures.forEach(ref => { if (ref.current?.open && !ref.current.contains(event.target)) ref.current.open = false; });
    const escape = event => {
      if (event.key !== "Escape") return;
      const open = disclosures.map(ref => ref.current).find(element => element?.open);
      if (open) { event.preventDefault(); open.open = false; open.querySelector("summary")?.focus(); }
    };
    document.addEventListener("click", outside);
    document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("click", outside); document.removeEventListener("keydown", escape); };
  }, []);
  useEffect(() => {
    if (!mobileOpen) return undefined;
    [spacesRef, accountRef].forEach(ref => { if (ref.current) ref.current.open = false; });
    const previousBody = document.body.style.overflow, previousHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    const background = [workspaceRef.current, dockRef.current, skipRef.current].filter(Boolean);
    closeRef.current?.focus();
    const previousBackground = background.map(element => ({ inert: element.inert, ariaHidden: element.getAttribute("aria-hidden") }));
    background.forEach(element => { element.inert = true; element.setAttribute("aria-hidden", "true"); });
    const keyboard = event => {
      if (event.key === "Escape") { event.preventDefault(); closeDrawer(true); return; }
      if (event.key !== "Tab" || !drawerRef.current) return;
      const items = Array.from(drawerRef.current.querySelectorAll('a[href],button:not([disabled]),[tabindex="0"]')).filter(element => element.tabIndex >= 0 && !element.closest("[hidden]") && element.getAttribute("aria-hidden") !== "true");
      const first = items[0], last = items[items.length - 1];
      if (!first || !last) { event.preventDefault(); drawerRef.current.focus(); return; }
      if (!drawerRef.current.contains(document.activeElement)) { event.preventDefault(); first.focus(); }
      else if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", keyboard, true);
    return () => {
      document.removeEventListener("keydown", keyboard, true);
      document.body.style.overflow = previousBody;
      document.documentElement.style.overflow = previousHtml;
      background.forEach((element, index) => {
        element.inert = previousBackground[index].inert;
        if (previousBackground[index].ariaHidden === null) element.removeAttribute("aria-hidden");
        else element.setAttribute("aria-hidden", previousBackground[index].ariaHidden);
      });
      if (returnFocus.current && openerRef.current?.isConnected) openerRef.current.focus();
    };
  }, [mobileOpen, closeDrawer]);

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true); setLogoutError("");
    try {
      const done = await logout();
      if (done !== true) throw new Error("Logout non confermato");
      navigate("/login", { replace: true });
    } catch (_) { setLogoutError("Uscita da QuoteOS non completata. Riprova."); }
    finally { setLoggingOut(false); }
  };
  const visibleNav = nav.filter(item => (!item.admin || administrator) && (!item.requires || (item.requires === "collaudo" && collaudo)));
  const sidebarProps = { items: visibleNav, current, links, companyName, onClose: closeDrawer, loggingOut, onLogout: handleLogout };
  const bottomItems = visibleNav.filter(item => ["dashboard", "rfqs", "quotes"].includes(item.key));

  return (
    <div className={`qos-shell${current.hideMobileDock ? " qos-focused-workspace" : ""}`} data-testid="quoteos-sofmat-shell" data-sf-section="quoteos">
      <a className="qos-skip" href="#qos-main" ref={skipRef}>Vai al contenuto</a>
      <Sidebar {...sidebarProps} />
      {mobileOpen && <div className="qos-mobile-layer">
        <button type="button" className="qos-backdrop" aria-label="Chiudi navigazione" data-testid="sidebar-backdrop" tabIndex={-1} onClick={() => closeDrawer(true)} />
        <Sidebar {...sidebarProps} mobile drawerRef={drawerRef} closeRef={closeRef} />
      </div>}
      <div className="qos-workspace" ref={workspaceRef}>
        <header className="qos-topbar" data-testid="mobile-topbar">
          <button type="button" className="qos-icon-button qos-menu-toggle" aria-label="Apri menu" aria-expanded={mobileOpen} aria-controls="qos-mobile-navigation" data-testid="sidebar-open-btn" ref={openerRef} onClick={() => { returnFocus.current = true; setMobileOpen(true); }}><Menu aria-hidden="true" size={22} /></button>
          <div className="qos-location">
            <details className="qos-space-switch" ref={spacesRef} onBlur={closeDisclosureOnBlur} onToggle={event => { if (event.currentTarget.open && accountRef.current) accountRef.current.open = false; }}>
              <summary aria-label="Cambia sezione: QuoteOS" data-testid="brand-logo-topbar"><FileText aria-hidden="true" size={20} /><span>QuoteOS</span><ChevronDown aria-hidden="true" size={14} /></summary>
              <nav className="qos-dropdown" aria-label="Spazi SOFMAT"><p className="qos-menu-caption">GLI SPAZI SOFMAT</p>
                {SOFMAT_SPACES.map(space => { const Icon = spaceIcons[space.key]; return space.key === "quoteos"
                  ? <Link key={space.key} to="/dashboard" className="qos-space-option is-current" aria-current="true" onClick={() => { spacesRef.current.open = false; }}><Icon aria-hidden="true" size={20} /><span><strong>{space.label}</strong><small>{space.description}</small></span><Check aria-hidden="true" size={18} /></Link>
                  : <a key={space.key} href={links[space.key]} className="qos-space-option"><Icon aria-hidden="true" size={20} /><span><strong>{space.label}</strong><small>{space.description}</small></span></a>; })}
              </nav>
            </details>
          </div>
          <nav className="qos-breadcrumbs" aria-label="Percorso della pagina">
            {current.crumbs.map((crumb, index) => <React.Fragment key={`${crumb.label}-${index}`}>{index > 0 && <ChevronRight aria-hidden="true" size={13} />}{crumb.to ? <Link to={crumb.to}>{crumb.label}</Link> : <span aria-current="page">{crumb.label}</span>}</React.Fragment>)}
          </nav>
          <div className="qos-top-actions">
            <SofmatOnboarding />
            <a className="qos-top-link" href={links.messages} aria-label="Messaggi SOFMAT" title="Messaggi SOFMAT"><Mail aria-hidden="true" size={20} /><span>Messaggi</span></a>
            <a className="qos-top-link qos-top-support" href={links.support} aria-label="Assistenza SOFMAT" title="Assistenza SOFMAT"><LifeBuoy aria-hidden="true" size={20} /></a>
            <details className="qos-account-switch" ref={accountRef} onBlur={closeDisclosureOnBlur} onToggle={event => { if (event.currentTarget.open && spacesRef.current) spacesRef.current.open = false; }}>
              <summary aria-label={`Account e azienda: ${companyName}`}>
                <span className="qos-account-avatar" aria-hidden="true">{Array.from(companyName)[0].toUpperCase()}</span>
                <span className="qos-account-label"><strong title={companyName}>{companyName}</strong><small title={personName}>{personName}</small></span><ChevronDown aria-hidden="true" size={14} />
              </summary>
              <div className="qos-dropdown qos-account-dropdown"><p className="qos-menu-caption">IL TUO ACCOUNT SOFMAT</p><p className="qos-account-person">{personName}</p><p className="qos-account-context">{companyName} · {roleLabel(user?.role)}</p>
                <a href={links.profile}>Profilo aziendale</a>{administrator && <a href={links.team}>Gestisci il team</a>}<a href={links.subscription}>Abbonamento SOFMAT</a><a href={links.account}>Vai al tuo spazio SOFMAT</a><a href={links.support}>Assistenza</a>
                <p className="qos-account-note">L’azienda e gli accessi si gestiscono dal portale SOFMAT.</p><button type="button" className="qos-account-exit" data-testid="btn-logout-account" onClick={handleLogout} disabled={loggingOut}><LogOut aria-hidden="true" size={17} />{loggingOut ? "Uscita in corso…" : "Esci da QuoteOS"}</button>
              </div>
            </details>
          </div>
        </header>
        {logoutError && <p className="qos-logout-error" role="alert">{logoutError}</p>}
        <main className="qos-main" id="qos-main" tabIndex={-1} ref={mainRef} aria-label={`QuoteOS · ${current.label}`}>
          <Outlet />
        </main>
        <footer className="qos-footer"><strong>SOFMAT</strong><span>La rete dell’officina.</span><span>Un progetto LL Capital Srl</span></footer>
      </div>
      {!current.hideMobileDock && <nav className="qos-mobile-dock" aria-label="Scorciatoie QuoteOS" ref={dockRef}>
        {bottomItems.map(({ key, to, label, icon: Icon }) => <Link key={key} to={to} className={current.active === key ? "is-active" : ""} aria-current={current.active === key ? "page" : undefined}><Icon aria-hidden="true" size={20} /><span>{label}</span></Link>)}
      </nav>}
    </div>
  );
}

